<script>
import api from '@/helpers/api'
import moment from 'moment'
import { sumBy } from 'lodash'
import VueApexCharts from 'vue-apexcharts'
import { authComputed } from '@/state/helpers'
import {
  foamQuantities,
  millQuantities,
  ordersByType,
  isDashboardDev
} from '@/router/views/dashboards/shared/dev-data'

const CHART_COLORS = [
  '#556ee6',
  '#34c38f',
  '#f1b44c',
  '#f46a6a',
  '#50a5f1',
  '#74788d',
  '#e83e8c',
  '#6f42c1',
  '#20c997',
  '#fd7e14',
  '#5b73e8',
  '#343a40'
]

function emptyBundle () {
  return { categories: [], series: [], totals: [] }
}

/** Spread a yearly total across months with light variance (dev mocks). */
function spreadOverMonths (total, seed = 0) {
  const months = 12
  const weights = Array.from({ length: months }, (_, i) => {
    const wave = 0.65 + 0.35 * Math.sin((i + seed) * 0.9)
    return Math.max(0.2, wave)
  })
  const weightSum = sumBy(weights)
  const raw = weights.map(w => (total * w) / weightSum)
  const floored = raw.map(v => Math.floor(v))
  let remainder = Math.round(total) - sumBy(floored)
  let i = 0
  while (remainder > 0) {
    floored[i % months] += 1
    remainder -= 1
    i += 1
  }
  return floored
}

export default {
  name: 'stats-charts-card',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    year: {
      type: Number,
      required: true
    },
    foamTags: {
      type: Array,
      default: () => []
    },
    millTags: {
      type: Array,
      default: () => []
    },
    foamMillTags: {
      type: Array,
      default: () => []
    },
    blockTags: {
      type: Array,
      default: () => []
    },
    foamMock: {
      type: Array,
      default: null
    },
    millMock: {
      type: Array,
      default: null
    },
    foamMillMock: {
      type: Array,
      default: null
    },
    blockMock: {
      type: Array,
      default: null
    }
  },
  data () {
    return {
      activeTab: 'types',
      isLoading: false,
      showLegend: false,
      cache: {
        types: emptyBundle(),
        foam: emptyBundle(),
        milling: emptyBundle(),
        foam_mill: emptyBundle(),
        block: emptyBundle()
      }
    }
  },
  computed: {
    ...authComputed,
    tabs () {
      const items = [
        {
          key: 'types',
          label: this.$t('stats.charts.types_eyebrow')
        },
        {
          key: 'foam',
          label: this.$t('stats.charts.foam_eyebrow')
        },
        {
          key: 'milling',
          label: this.$t('stats.charts.milling_eyebrow')
        },
        {
          key: 'foam_mill',
          label: this.$t('stats.charts.foam_mill_eyebrow')
        }
      ]
      if (this.userRole === 'admin') {
        items.push({
          key: 'block',
          label: this.$t('stats.charts.block_eyebrow')
        })
      }
      return items
    },
    activeTitle () {
      const map = {
        types: this.$t('stats.breakdown.types_title'),
        foam: this.$t('stats.charts.foam'),
        milling: this.$t('stats.charts.milling'),
        foam_mill: this.$t('stats.charts.foam_mill'),
        block: this.$t('stats.charts.block')
      }
      return map[this.activeTab]
    },
    activeBundle () {
      return this.cache[this.activeTab] || emptyBundle()
    },
    monthCategories () {
      return Array.from({ length: 12 }, (_, i) =>
        moment()
          .year(this.year)
          .month(i)
          .format('MMM')
      )
    },
    total () {
      return sumBy(this.activeBundle.totals || [], 'quantity')
    },
    legendRows () {
      return (this.activeBundle.totals || []).map((row, index) => {
        const quantity = Number(row.quantity) || 0
        return {
          key: `${row.name}-${index}`,
          label: row.name,
          quantity,
          share: this.total
            ? `${Math.round((quantity / this.total) * 100)}%`
            : '0%',
          color: CHART_COLORS[index % CHART_COLORS.length]
        }
      })
    },
    isTreemapTab () {
      return ['foam', 'milling', 'foam_mill'].includes(this.activeTab)
    },
    chartSeries () {
      if (this.isTreemapTab) {
        return [
          {
            data: (this.activeBundle.totals || []).map(row => ({
              x: row.name,
              y: Number(row.quantity) || 0
            }))
          }
        ]
      }
      return this.activeBundle.series || []
    },
    chartType () {
      return this.isTreemapTab ? 'treemap' : 'bar'
    },
    chartOptions () {
      if (this.isTreemapTab) {
        return {
          chart: {
            type: 'treemap',
            toolbar: { show: false },
            animations: { enabled: true, speed: 400 },
            parentHeightOffset: 0,
            sparkline: { enabled: false }
          },
          colors: CHART_COLORS,
          legend: { show: false },
          grid: {
            padding: {
              top: 12,
              right: 16,
              bottom: 12,
              left: 16
            }
          },
          plotOptions: {
            treemap: {
              distributed: true,
              enableShades: false
            }
          },
          dataLabels: {
            enabled: true,
            style: {
              fontSize: '11px',
              fontWeight: 500,
              colors: ['#fff']
            },
            formatter (text, opts) {
              const value = opts && opts.value != null ? opts.value : ''
              return [text, value]
            }
          },
          tooltip: {
            y: {
              formatter: val => `${val}`
            }
          }
        }
      }

      return {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: { show: false },
          animations: { enabled: true, speed: 400 }
        },
        colors: CHART_COLORS,
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '62%',
            borderRadius: 2,
            dataLabels: {
              total: {
                enabled: true,
                style: {
                  fontSize: '10px',
                  fontWeight: 600,
                  color: '#64748b'
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        legend: { show: false },
        grid: {
          borderColor: '#eef2f7',
          padding: { top: 8 }
        },
        xaxis: {
          categories: this.activeBundle.categories || this.monthCategories,
          labels: {
            style: { colors: '#64748b', fontSize: '11px' }
          }
        },
        yaxis: {
          labels: {
            style: { colors: '#94a3b8', fontSize: '11px' }
          }
        },
        tooltip: {
          y: {
            formatter: val => `${val}`
          }
        },
        fill: { opacity: 0.95 }
      }
    },
    chartHeight () {
      if (this.isTreemapTab) {
        const rows = (this.activeBundle.totals || []).length || 4
        return Math.max(280, Math.min(420, rows * 28 + 80))
      }
      return 300
    },
    startOfYear () {
      return moment()
        .year(this.year)
        .startOf('year')
        .toDate()
    },
    endOfYear () {
      if (this.year === moment().year()) return moment().toDate()
      return moment()
        .year(this.year)
        .endOf('year')
        .toDate()
    }
  },
  watch: {
    year: {
      immediate: true,
      handler: 'loadAll'
    },
    userProfile: 'loadAll',
    userRole (role) {
      if (role !== 'admin' && this.activeTab === 'block') {
        this.activeTab = 'types'
      }
    }
  },
  methods: {
    selectTab (key) {
      this.activeTab = key
    },
    toggleLegend () {
      this.showLegend = !this.showLegend
    },
    monthQuantitiesFromRows (rows) {
      const byMonth = {}
      ;(rows || []).forEach(row => {
        if (Number(row.year) !== Number(this.year)) return
        const month = Number(row.month)
        if (!month) return
        byMonth[month] =
          (byMonth[month] || 0) + (Number(row.quantity) || 0)
      })
      return Array.from({ length: 12 }, (_, i) => byMonth[i + 1] || 0)
    },
    buildBundleFromSeries (namedSeries) {
      const series = (namedSeries || [])
        .map(item => ({
          name: item.name,
          data: (item.data || []).map(v => Number(v) || 0)
        }))
        .filter(item => sumBy(item.data) > 0)

      const totals = series.map(item => ({
        name: item.name,
        quantity: sumBy(item.data)
      }))

      return {
        categories: this.monthCategories,
        series,
        totals
      }
    },
    async loadAll () {
      this.isLoading = true
      await Promise.all([
        this.loadTypeBundle(),
        this.loadTagBundle(
          'foam',
          this.foamTags,
          this.foamMock || foamQuantities
        ),
        this.loadTagBundle(
          'milling',
          this.millTags,
          this.millMock || millQuantities
        ),
        this.loadTagBundle('foam_mill', this.foamMillTags, this.foamMillMock, {
          hideEmpty: true
        }),
        this.userRole === 'admin'
          ? this.loadTagBundle('block', this.blockTags, this.blockMock, {
              hideEmpty: true
            })
          : Promise.resolve()
      ])
      this.isLoading = false
    },
    typeLabel (type) {
      const key = `forms.${type}`
      const translated = this.$t(key)
      if (translated !== key) return translated
      return type
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    },
    async loadTypeBundle () {
      let stats = []
      try {
        stats = await api.webForms.fetchModelTypeStats({
          year: this.year
        })
      } catch (e) {
        stats = []
      }

      const byType = {}
      ;(stats || []).forEach(row => {
        if (!row.type) return
        if (!byType[row.type]) byType[row.type] = []
        byType[row.type].push(row)
      })

      let namedSeries = Object.keys(byType).map(type => ({
        name: this.typeLabel(type),
        data: this.monthQuantitiesFromRows(byType[type]),
        key: type
      }))

      const liveTotal = sumBy(namedSeries, s => sumBy(s.data))
      if (isDashboardDev() && liveTotal === 0) {
        namedSeries = ordersByType.map((row, index) => ({
          name: this.typeLabel(row.type),
          data: spreadOverMonths(row.quantity, index),
          key: row.type
        }))
      } else if (isDashboardDev()) {
        const liveKeys = new Set(namedSeries.map(s => s.key))
        ordersByType.forEach((row, index) => {
          if (liveKeys.has(row.type)) return
          namedSeries.push({
            name: this.typeLabel(row.type),
            data: spreadOverMonths(row.quantity, index),
            key: row.type
          })
        })
      }

      namedSeries.sort(
        (a, b) => sumBy(b.data) - sumBy(a.data)
      )
      this.$set(this.cache, 'types', this.buildBundleFromSeries(namedSeries))
    },
    async loadTagBundle (key, tags, mockSeries, options = {}) {
      const { hideEmpty = false } = options
      const tagList = tags || []
      const hasMock =
        isDashboardDev() && Array.isArray(mockSeries) && mockSeries.length

      const profile = this.userProfile || {}
      const { role, customer_account_id } = profile

      let namedSeries = tagList.map((tag, index) => ({
        name: tag.label,
        data: Array(12).fill(0),
        index
      }))

      const canFetch =
        role && !(role === 'user' && customer_account_id === null)

      if (canFetch) {
        try {
          const results = await Promise.all(
            tagList.map(tag =>
              api.itemTagStatPerWeek.fetchSeries({
                start_date: this.startOfYear,
                end_date: this.endOfYear,
                tags: tag.tags,
                customer_id: customer_account_id
              })
            )
          )
          namedSeries = tagList.map((tag, index) => ({
            name: tag.label,
            data: this.monthQuantitiesFromRows(results[index]),
            index
          }))
        } catch (e) {
          namedSeries = tagList.map((tag, index) => ({
            name: tag.label,
            data: Array(12).fill(0),
            index
          }))
        }
      }

      const liveTotal = sumBy(namedSeries, s => sumBy(s.data))
      if (hasMock && liveTotal === 0) {
        namedSeries = tagList.map((tag, index) => ({
          name: tag.label,
          data: spreadOverMonths(Number(mockSeries[index]) || 0, index),
          index
        }))
      }

      if (hideEmpty) {
        namedSeries = namedSeries.filter(s => sumBy(s.data) > 0)
      }

      namedSeries.sort(
        (a, b) => sumBy(b.data) - sumBy(a.data)
      )
      this.$set(this.cache, key, this.buildBundleFromSeries(namedSeries))
    }
  }
}
</script>

<template>
  <div class="portal-card portal-card--panel">
    <div class="portal-card__eyebrow">{{ $t('stats.charts.card_eyebrow') }}</div>
    <div class="portal-card__panel-title mb-2">{{ activeTitle }}</div>

    <div class="stats-charts__toolbar mb-2">
      <div class="stats-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="stats-tabs__btn"
          :class="{ 'is-active': activeTab === tab.key }"
          @click="selectTab(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>
      <button
        type="button"
        class="stats-charts__legend-btn"
        @click="toggleLegend"
      >
        <i class="mdi mdi-table" aria-hidden="true"></i>
        {{
          showLegend
            ? $t('stats.charts.hide_legend')
            : $t('stats.charts.show_legend')
        }}
      </button>
    </div>

    <div v-if="isLoading" class="text-muted small py-3">
      {{ $t('Loading...') }}
    </div>

    <div v-else class="stats-charts__stack">
      <div class="stats-charts__chart">
        <apexchart
          :key="`chart-${activeTab}-${year}`"
          :type="chartType"
          :height="chartHeight"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>

      <div v-if="showLegend" class="stats-charts__legend">
        <table v-if="legendRows.length">
          <thead>
            <tr>
              <th>{{ $t('dashboard.activity.col_type') }}</th>
              <th class="text-right">{{ $t('quantity') }}</th>
              <th class="text-right">{{ $t('dashboard.activity.col_share') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in legendRows" :key="row.key">
              <td>
                <span
                  class="stats-charts__swatch"
                  :style="{ background: row.color }"
                ></span>
                <span>{{ row.label }}</span>
              </td>
              <td class="text-right stats-charts__qty">{{ row.quantity }}</td>
              <td class="text-right text-muted">{{ row.share }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>{{ $t('Total') }}</td>
              <td class="text-right stats-charts__qty">{{ total }}</td>
              <td class="text-right text-muted">100%</td>
            </tr>
          </tfoot>
        </table>
        <p v-else class="text-muted small mb-0">
          {{ $t('dashboard.activity.empty_table') }}
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.portal-card--panel {
  background: #fff;
  border-radius: 0.85rem;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
  padding: 0.85rem 1rem;
  min-width: 0;
}

.portal-card__eyebrow {
  color: #94a3b8;
  font-size: 0.72rem;
  font-weight: 500;
  margin-bottom: 0.15rem;
}

.portal-card__panel-title {
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 600;
}

.stats-charts__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.stats-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  min-width: 0;
  flex: 1 1 auto;
}

.stats-tabs__btn {
  appearance: none;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: #0f172a;
    border-color: #cbd5e1;
  }

  &.is-active {
    background: #0f172a;
    border-color: #0f172a;
    color: #fff;
  }
}

.stats-charts__stack {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  width: 100%;

  .stats-charts__chart,
  .stats-charts__legend {
    padding: 0.75rem 0.5rem 1rem;
  }
}

.stats-charts__chart {
  min-width: 0;
  width: 100%;
}

.stats-charts__legend-btn {
  appearance: none;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #0f172a;
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  flex: 0 0 auto;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;

  i {
    font-size: 1rem;
    line-height: 1;
  }

  &:hover {
    background: #0f172a;
    border-color: #0f172a;
    color: #fff;
  }
}

.stats-charts__legend {
  min-width: 0;
  max-width: 40rem;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.72rem;
  }

  th,
  td {
    padding: 0.22rem 0.15rem;
    border-bottom: 1px solid #eef2f7;
    vertical-align: middle;
  }

  th {
    color: #94a3b8;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-size: 0.62rem;
  }

  tfoot td {
    border-bottom: 0;
    padding-top: 0.35rem;
    font-weight: 500;
    color: #0f172a;
  }
}

.stats-charts__swatch {
  display: inline-block;
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  margin-right: 0.3rem;
  vertical-align: middle;
}

.stats-charts__qty {
  color: #0f172a;
  font-weight: 500;
}
</style>
